import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { FiSiteDesignHelperService } from '../../fi-site-design-helper.service';
import { Animations } from '@fi-shared/utils/animations';
import { ISiteDesign, ILightWeightObject } from '@fi-shared/models';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiSiteDesignService } from '@fi-shared/fi-shared-services/fi-site-design.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';

export interface IPageLayoutCardData {
    cardTitle: string;
    cardDescription: string;
    sref: string;
    srefParams: { [prop: string]: any };
}

@Component({
    selector: 'fi-site-design-brand-editor',
    templateUrl: './fi-site-design-brand-editor.component.html',
    styleUrls: ['./fi-site-design-brand-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    animations: [Animations.enterState(200)],
    host: { class: 'fi-site-design-brand-editor' },
    standalone: false,
})
export class FiSiteDesignBrandEditorComponent implements OnInit, OnDestroy {
    @Input() siteDesign: ISiteDesign;
    @Input() availableBusinessAreas: ILightWeightObject[];
    isBrandSettingsChanged: boolean = false;
    pageLayoutCardsData: IPageLayoutCardData[];

    constructor(
        private fiSiteDesignHelperService: FiSiteDesignHelperService,
        private fiSiteDesignService: FiSiteDesignService,
        private cdr: ChangeDetectorRef,
        private saveChangesWarningService: SaveChangesWarningService,
        private detectDevisesService: DetectDevicesService,
        private appTextService: AppTextService,
    ) {}

    ngOnInit(): void {
        this.setPageLayoutCardsData();
        this.subscribeOnLoginSettingsChanges();
        this.saveChangesWarningService.start(() => this.siteDesign);
    }

    private setPageLayoutCardsData() {
        this.pageLayoutCardsData = [
            {
                cardTitle: this.appTextService.get('site_design.home_page_design'),
                cardDescription: this.appTextService.get('site_design.home_page_design_description'),
                sref: FiStateNames.Appearance.StartPage,
                srefParams: { siteDesignId: this.siteDesign.Id },
            },
            {
                cardTitle: this.appTextService.get('site_design.sidebar_settings'),
                cardDescription: this.appTextService.get('site_design.sidebar_settings_description'),
                sref: FiStateNames.Appearance.Sidebar.Edit,
                srefParams: { siteDesignId: this.siteDesign.Id },
            },
            {
                cardTitle: this.appTextService.get('site_design.list_view_settings'),
                cardDescription: this.appTextService.get('site_design.list_view_settings_description'),
                sref: FiStateNames.Appearance.ListView.Edit,
                srefParams: { siteDesignId: this.siteDesign.Id },
            },
        ];
    }

    isMobilePhone(): boolean {
        return this.detectDevisesService.isMobilePhone();
    }

    cancel() {
        if (this.saveChangesWarningService.hasChanges()) {
            this.saveChangesWarningService.showConfirmDialog().onYesButtonClick(() => this.back());
        } else {
            this.back();
        }
    }

    onBusinessAreaIdsChanged($event: number[]) {
        this.isBrandSettingsChanged = true;
    }

    save() {
        this.fiSiteDesignService.saveSiteDesign(this.siteDesign).then(() => this.back());
    }

    private back() {
        this.fiSiteDesignHelperService.setBrandSettingsChanged(false);
        this.saveChangesWarningService.stop();
        window.history.back();
    }

    private subscribeOnLoginSettingsChanges() {
        this.fiSiteDesignHelperService
            .isBrandSettingsChanged$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((value: boolean) => {
                this.isBrandSettingsChanged = value;
                this.cdr.markForCheck();
            });
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
